<script>
import { h } from 'vue'
import { getFullCloudfrontImageUrl, getCloudfrontUrl, platformLogo } from 'give-a-day-shared/utils/s3Path.mjs'
import { IMAGE_FORMATS } from 'give-a-day-shared/utils/imageFormats.mjs'
import { isSet } from 'give-a-day-shared/utils/isSet.mjs'
import { removeAllHtmlTags, safeTrim } from 'give-a-day-shared/utils/text.mjs'

// Google recommends keeping article headlines to 110 characters or fewer.
const MAX_HEADLINE_LENGTH = 110

// Emits a schema.org BlogPosting JSON-LD block for a blog detail page, so search engines
// can show rich results. Modeled on the other Google*Json components in this folder.
export default {
  name: 'GoogleBlogPostingJson',
  props: {
    post: { type: Object, required: true },
  },
  computed: {
    allDataLooksGood () {
      return !!(this.post && this.post.id && this.post.localisation && this.post.localisation.title && this.post.localisation.slug)
    },
  },
  methods: {
    toIso (date) {
      if (!isSet(date)) return null
      const parsed = new Date(date)
      return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString()
    },
    canonicalUrl () {
      const path = this.$findPath('/blog/:slug/:id', { slug: this.post.localisation.slug, id: this.post.id })
      return `${this.$globalState.baseUrl}${path}`
    },
    // A platform-authored post is published under that platform; a globally authored
    // (super admin) post is published under Give a Day.
    buildPublisher () {
      const platform = this.$globalState.platform
      if (!this.post.isGlobal && platform && platform.id) {
        const publisher = { '@type': 'Organization', name: this.$getPlatformDisplayName() || 'Give a Day' }
        if (platform.avatarExistsOnS3) {
          const format = IMAGE_FORMATS.platform_logo
          const width = format.widths[format.widths.length - 1]
          publisher.logo = {
            '@type': 'ImageObject',
            url: getFullCloudfrontImageUrl(platformLogo(platform.id), width, 'png', platform.updated),
          }
        }
        return publisher
      }
      return {
        '@type': 'Organization',
        name: 'Give a Day',
        logo: { '@type': 'ImageObject', url: getCloudfrontUrl('/assets/img/general/logo.svg') },
      }
    },
  },
  render () {
    if (!this.allDataLooksGood) {
      return h('script') // Render empty script. Nothing else to do
    }

    const loc = this.post.localisation
    const url = this.canonicalUrl()
    const publisher = this.buildPublisher()

    const json = {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      mainEntityOfPage: { '@type': 'WebPage', '@id': url },
      headline: safeTrim(loc.title).slice(0, MAX_HEADLINE_LENGTH),
      datePublished: this.toIso(this.post.publishedAt),
      dateModified: this.toIso(loc.updated) || this.toIso(this.post.publishedAt),
      publisher,
    }

    // Author: the person who wrote it, falling back to the publishing organisation. This uses the
    // bare personal name, not the byline's display name (which credits Give a Day for a post by
    // the team), because a schema.org Person must not carry an organisation prefix.
    const authorName = safeTrim(this.post.author?.personName)
    json.author = authorName
      ? { '@type': 'Person', name: authorName }
      : { '@type': 'Organization', name: publisher.name }

    const description = safeTrim(removeAllHtmlTags(loc.metaDescription || loc.excerpt || ''))
    if (description) json.description = description

    // Cover image is stored as a full URL; schema.org expects an absolute URL.
    if (loc.coverImage) json.image = [loc.coverImage]

    return h('script', { type: 'application/ld+json', innerHTML: JSON.stringify(json) })
  },
}
</script>
